import { ChangeDetectionStrategy, Component, computed, input, model, output, signal } from '@angular/core';
import { DsButtonModule, DsIconModule, DsTooltipDirectiveModule } from '@bmw-ds/components';

import { Customer } from '../../../customer/model/customer.model';
import { useI18n } from '../../../i18n/services/i18n.service';
import { SyncStatusComponent } from '../sync-status/sync-status.component';
import { SyncWithNscModalWrapper } from '../sync-with-nsc/sync-with-nsc-modal.wrapper';

@Component({
  selector: 'cdf-profile-sync',
  imports: [DsIconModule, DsButtonModule, DsTooltipDirectiveModule, SyncWithNscModalWrapper, SyncStatusComponent],
  template: `
    <cdf-sync-status [syncStatus]="inSync() ? 'NSC_RETAIL_SYNCED' : 'RETAIL_ONLY'" />
    <!-- accountNumber has to be defined for cdf-sync-with-nsc-modal. At this point it always should be defined anyways,
   as every retail CRM customer has an accountNumber. If no retail customer is loaded into the customer profile page yet,
   this component is not displayed -->
    @if (customer()?.accountNumber; as accountNumber) {
      <div class="sync-to-nsc">
        <ds-button
          (click)="syncWithNscModalOpen.set(true)"
          variant="outline"
          [disabled]="syncButtonDisabled()"
          [ds-tooltip]="getTooltipText()"
          [dsTooltipConfig]="{
            origin: 'bottom',
            disabled: !getTooltipText(),
            showTriggerDelay: 500,
          }"
          >{{ i18n('nscSync.button') }}
        </ds-button>
      </div>
      <cdf-sync-with-nsc-modal
        [(open)]="syncWithNscModalOpen"
        [initialEmail]="customerEmail()"
        [accountNumber]="accountNumber"
        (reloadPage)="reloadPage.emit()"
      />
    }
  `,
  styleUrl: './profile-sync.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileSyncComponent {
  readonly i18n = useI18n();
  readonly syncWithNscModalOpen = signal(false);

  readonly inSync = input.required<boolean>();
  readonly customer = input.required<Customer | undefined>();
  readonly readonly = input(false);

  readonly successfullyInitializedIdCreation = model<boolean>();

  readonly reloadPage = output();

  // Sync button is disabled if account already is in sync or no emails are set which are required for syncing
  protected readonly syncButtonDisabled = computed(() => this.inSync() || !this.customerEmail() || this.readonly());

  protected readonly customerEmail = computed(
    () => this.customer()?.privateContact.email || this.customer()?.businessContact.email,
  );

  protected readonly getTooltipText = computed(() => {
    if (this.readonly()) {
      return this.i18n('readonlyTooltip');
    }

    if (!this.customerEmail()) {
      return this.i18n('nscSync.disabledButtonTooltip');
    }

    return '';
  });
}
